// src/components/products/ProductForm/Step3GeneralInfo.jsx

import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LanguageToggle from "../../global/LanguageToggle";
import toast from "react-hot-toast";

// Import hooks
import useGetServicesBySuppliers from "../../../hooks/services/useGetServicesBySuppliers";
import useGetCategoriesForSelect from "../../../hooks/categories/useGetCategoriesForSelect";
import useGetTransportationsForSelect from "../../../hooks/transportations/useGetTransportationsForSelect";
import useGetSuppliersForSelect from "../../../hooks/suppliers/useGetSuppliersForSelect";
import useGetPricingUnitsForSelect from "../../../hooks/pricingUnits/useGetPricingUnitsForSelect";
import { useUploadMedia } from "../../../hooks/media/useUploadMedia";

// ✅ Import Custom Hooks & Helpers
import useFormLanguage from "../../../hooks/forms/useFormLanguage";
import { handleMultiLangChange as multiLangHelper } from "../../../utils/forms/multiLangHelper";
import { createAccordionHandler, isPanelExpanded } from "../../../utils/forms/accordionHelper";
import {
    transformOptionsToIdName,
    transformPricingUnits,
    transformServices
} from "../../../utils/forms/transformHelper";
import { isValidImageType, isValidFileSize } from "../../../utils/forms/validationHelper";

// Import Section Components
import BasicClassificationSection from "./GeneralInfoSections/BasicClassificationSection";
import SupplierServiceSection from "./GeneralInfoSections/SupplierServiceSection";
import PricingOperationsSection from "./GeneralInfoSections/PricingOperationsSection";
import DescriptionsSection from "./GeneralInfoSections/DescriptionsSection";
import InclusionsExclusionsSection from "./GeneralInfoSections/InclusionsExclusionsSection";
import PoliciesSection from "./GeneralInfoSections/PoliciesSection";
import MediaSection from "./GeneralInfoSections/MediaSection";
import NotFoundType from "./NotFoundType";

const Step3GeneralInfo = ({
    formData,
    setFormData,
    errors = {},
    isEdit = false,
    currentData = null
}) => {
    // ✅ استخدام Custom Hook للغة
    const { isArabic, activeLang, setActiveLang } = useFormLanguage();

    // ✅ Accordion state - يدعم فتح عدة أكورديونات في نفس الوقت
    const [expandedPanels, setExpandedPanels] = useState(['panel1']);
    const handleAccordionChange = createAccordionHandler(setExpandedPanels, true); // ✅ allowMultiple = true

    // Selected supplier (for services filter)
    const [selectedSupplier, setSelectedSupplier] = useState(null);

    // Upload media hook
    const { uploadMedia, isPending: isUploadingCover } = useUploadMedia();

    // Get data from hooks
    const { options: categories, isLoading: loadingCategories } = useGetCategoriesForSelect();
    const { transportations, isLoading: loadingTransportations } = useGetTransportationsForSelect();
    const { suppliers, isLoading: loadingSuppliers } = useGetSuppliersForSelect();
    const { options: pricingUnits, isLoading: loadingPricingUnits } = useGetPricingUnitsForSelect();

    // Get services filtered by supplier
    const { services, isLoading: loadingServices } = useGetServicesBySuppliers(
        selectedSupplier ? [selectedSupplier] : [],
        !!selectedSupplier
    );

    // ✅ استخدام Transform Helpers
    const categoriesOptions = useMemo(() => transformOptionsToIdName(categories), [categories]);
    const transportationsOptions = useMemo(() => transformOptionsToIdName(transportations), [transportations]);
    const suppliersOptions = useMemo(() => transformOptionsToIdName(suppliers), [suppliers]);
    const pricingUnitsOptions = useMemo(() => transformPricingUnits(pricingUnits), [pricingUnits]);
    const servicesOptions = useMemo(() => transformServices(services), [services]);

    // Set supplier from currentData in edit mode
    useEffect(() => {
        if (isEdit && currentData?.suppliers && currentData.suppliers.length > 0) {
            const supplierId = currentData.suppliers[0].id;
            setSelectedSupplier(supplierId);
            setFormData(prev => ({
                ...prev,
                supplier_ids: [supplierId]
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, currentData]);
    const productType = formData.selectedProductType;
    const productTypeName = productType?.name?.en || productType?.name || '';

    const isTransfer = productTypeName.toLowerCase().includes('transfer');
    const isTour = productTypeName.toLowerCase().includes('tour');

    if (!isTransfer && !isTour) return <NotFoundType productTypeName={productTypeName} />;

    // ✅ استخدام Multi-lang Helper
    const handleFieldChange = (field, lang, value) => {
        multiLangHelper(setFormData, formData, field, lang, value);
    };

    // Handle supplier change
    const handleSupplierChange = (e) => {
        const supplierId = e.target.value;
        setSelectedSupplier(supplierId);

        setFormData({
            ...formData,
            supplier_ids: [supplierId],
            service_id: isEdit ? formData.service_id : null,
            title: isEdit ? formData.title : { ar: '', en: '' },
        });
    };

    // Handle service change (auto-fill title)
    const handleServiceChange = (e) => {
        const serviceId = e.target.value;
        const service = servicesOptions.find((s) => s.id === serviceId);

        let titleAr = '';
        let titleEn = '';

        if (service && service.name) {
            if (typeof service.name === 'object') {
                titleAr = service.name.ar || '';
                titleEn = service.name.en || '';
            } else {
                titleAr = service.name;
                titleEn = service.name;
            }
        }

        setFormData({
            ...formData,
            service_id: serviceId,
            title: { ar: titleAr, en: titleEn },
        });
    };

    // Handle pricing unit change (auto-fill price_type)
    const handlePricingUnitChange = (e) => {
        const pricingUnitId = e.target.value;
        const pricingUnit = pricingUnitsOptions.find((p) => p.id === pricingUnitId);

        let priceTypeAr = '';
        let priceTypeEn = '';

        if (pricingUnit && pricingUnit.name) {
            if (typeof pricingUnit.nameAE === 'object') {
                priceTypeAr = pricingUnit.nameAE.ar || '';
                priceTypeEn = pricingUnit.nameAE.en || '';
            } else {
                priceTypeAr = pricingUnit.name;
                priceTypeEn = pricingUnit.name;
            }
        }

        setFormData({
            ...formData,
            pricing_unit_id: pricingUnitId,
            price_type: { ar: priceTypeAr, en: priceTypeEn },
        });
    };

    // Handle cover image change
    const handleCoverImageChange = (event) => {
        const file = event.target.files?.[0];

        setFormData(prev => ({
            ...prev,
            coverImageError: '',
            uploadingCoverImage: false,
            coverImageSuccess: ''
        }));

        if (!file) return;

        // ✅ استخدام Validation Helpers
        if (!isValidImageType(file)) {
            const errorMsg = isArabic
                ? 'يرجى اختيار صورة بصيغة صحيحة (JPG, PNG, WEBP)'
                : 'Please select a valid image format (JPG, PNG, WEBP)';

            setFormData(prev => ({ ...prev, coverImageError: errorMsg }));
            toast.error(errorMsg);
            return;
        }

        if (!isValidFileSize(file, 5)) {
            const errorMsg = isArabic
                ? 'حجم الصورة يجب أن لا يتجاوز 5 ميجابايت'
                : 'Image size must not exceed 5MB';

            setFormData(prev => ({ ...prev, coverImageError: errorMsg }));
            toast.error(errorMsg);
            return;
        }

        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        setFormData(prev => ({
            ...prev,
            cover_image_preview: URL.createObjectURL(file),
            uploadingCoverImage: true
        }));

        uploadMedia(uploadFormData, {
            onSuccess: (res) => {
                const fileName = res?.data?.file_name || res?.data?.name || res?.file_name || '';

                if (!fileName) {
                    const errorMsg = isArabic
                        ? 'تعذر الحصول على اسم الملف من الخادم'
                        : 'Cannot get uploaded file name from server';

                    setFormData(prev => ({
                        ...prev,
                        uploadingCoverImage: false,
                        coverImageError: errorMsg
                    }));
                    toast.error(errorMsg);
                    return;
                }

                setFormData(prev => ({
                    ...prev,
                    cover_image: fileName,
                    uploadingCoverImage: false,
                    coverImageSuccess: isArabic ? 'تم رفع الصورة بنجاح' : 'Image uploaded successfully',
                    coverImageError: ''
                }));

                toast.success(
                    isArabic ? 'تم رفع صورة الغلاف بنجاح' : 'Cover image uploaded successfully'
                );
            },
            onError: (error) => {
                console.error('Cover image upload error:', error);

                const errorMessage = error?.response?.data?.message ||
                    error?.message ||
                    (isArabic ? 'فشل رفع الصورة، الرجاء المحاولة مرة أخرى' : 'Failed to upload image, please try again');

                setFormData(prev => ({
                    ...prev,
                    uploadingCoverImage: false,
                    coverImageError: errorMessage,
                    coverImageSuccess: '',
                    cover_image: null,
                    cover_image_preview: ''
                }));

                toast.error(errorMessage);
            }
        });
    };

    // Handle multi images change
    const handleMultiImagesChange = (images) => {
        const fileNames = images.map((img) => img.file_name || img.url);
        setFormData({
            ...formData,
            images: fileNames,
            uploadedImages: images,
        });
    };

    // Shared props for all sections
    const commonProps = {
        formData,
        setFormData,
        errors,
        isArabic,
        activeLang,
        handleMultiLangChange: handleFieldChange,
    };

    return (
        <Paper sx={{ p: 4 }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                }}
            >
                <Box>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                        {isArabic ? 'معلومات المنتج العامة' : 'General Product Information'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isArabic
                            ? 'أدخل المعلومات الأساسية للمنتج'
                            : 'Enter basic product information'}
                    </Typography>
                </Box>

                <LanguageToggle
                    activeLang={activeLang}
                    onChange={(lang) => setActiveLang(lang)}
                    showLabel={true}
                />
            </Box>

            {/* Accordions */}
            <Box>
                {/* 1. Basic Classification */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel1')}
                    onChange={handleAccordionChange('panel1')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📋 التصنيف الأساسي' : '📋 Basic Classification'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <BasicClassificationSection
                            {...commonProps}
                            categoriesOptions={categoriesOptions}
                            transportationsOptions={transportationsOptions}
                            loadingCategories={loadingCategories}
                            loadingTransportations={loadingTransportations}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* 2. Supplier & Service */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel2')}
                    onChange={handleAccordionChange('panel2')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '🏢 المورد والخدمة' : '🏢 Supplier & Service'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <SupplierServiceSection
                            {...commonProps}
                            selectedSupplier={selectedSupplier}
                            handleSupplierChange={handleSupplierChange}
                            handleServiceChange={handleServiceChange}
                            suppliersOptions={suppliersOptions}
                            servicesOptions={servicesOptions}
                            loadingSuppliers={loadingSuppliers}
                            loadingServices={loadingServices}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* 3. Pricing & Operations */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel3')}
                    onChange={handleAccordionChange('panel3')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '💰 التسعير والتشغيل' : '💰 Pricing & Operations'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <PricingOperationsSection
                            {...commonProps}
                            handlePricingUnitChange={handlePricingUnitChange}
                            pricingUnitsOptions={pricingUnitsOptions}
                            loadingPricingUnits={loadingPricingUnits}
                        />
                    </AccordionDetails>
                </Accordion>

                {/* 4. Descriptions */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel4')}
                    onChange={handleAccordionChange('panel4')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📝 الأوصاف' : '📝 Descriptions'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <DescriptionsSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 5. Inclusions & Exclusions */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel5')}
                    onChange={handleAccordionChange('panel5')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '✅ التضمينات والاستثناءات' : '✅ Inclusions & Exclusions'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <InclusionsExclusionsSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 6. Policies */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel6')}
                    onChange={handleAccordionChange('panel6')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '📜 السياسات' : '📜 Policies'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <PoliciesSection {...commonProps} />
                    </AccordionDetails>
                </Accordion>

                {/* 7. Media */}
                <Accordion
                    expanded={isPanelExpanded(expandedPanels, 'panel7')}
                    onChange={handleAccordionChange('panel7')}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {isArabic ? '🖼️ الصور' : '🖼️ Media'}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <MediaSection
                            {...commonProps}
                            handleCoverImageChange={handleCoverImageChange}
                            handleMultiImagesChange={handleMultiImagesChange}
                            isUploadingCover={isUploadingCover}
                            isEdit={isEdit}
                        />
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Paper>
    );
};

export default Step3GeneralInfo;
